import { zodResolver } from '@hookform/resolvers/zod';
import { AllExamCenterSearchSelection } from '@report/exam-center-report-management';
import { useGetAllExamCentersQuery, useGetAllExamCoursesQuery } from '@report/graphql';
import {
  Box,
  Button,
  Divider,
  Flex,
  Form,
  FormDateInput,
  FormSearchMultipleSelectionWithSelectAllOption,
  Grid,
  Text,
} from '@shared/components';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useQuestionnaireReportSearchParams } from '../../hooks';

import {
  QuestionnaireReportFilterSchema,
  type QuestionnaireReportFilterFormInput,
  type QuestionnaireReportFilterFormOutput,
} from './schema';

export default function QuestionnaireReportFilterSection() {
  const [
    { examStartDate, examEndDate, examCenter, examCourse },
    { changeFilters, resetAllParams },
  ] = useQuestionnaireReportSearchParams();

  const formContext = useForm<
    QuestionnaireReportFilterFormInput,
    unknown,
    QuestionnaireReportFilterFormOutput
  >({
    resolver: zodResolver(QuestionnaireReportFilterSchema),
    defaultValues: {
      examStartDate: null,
      examEndDate: null,
      examCenter: [],
      examCourse: [],
    },
  });

  const { refetch: refetchExamCenters } = useGetAllExamCentersQuery({
    fetchPolicy: 'cache-first',
  });
  const { data: examCourseData, loading: isLoadingExamCourses } = useGetAllExamCoursesQuery({
    fetchPolicy: 'cache-first',
  });

  const examCourseOptions = useMemo(
    () =>
      examCourseData?.reportGetAllExamCourses?.examCourses.map((c) => ({
        label: c.code,
        value: c.code,
      })) ?? [],
    [examCourseData?.reportGetAllExamCourses?.examCourses],
  );

  const getFormValuesFromSearchParams = useCallback(async () => {
    if (!examStartDate && !examEndDate && examCenter.length === 0 && examCourse.length === 0) {
      return {
        examStartDate: null,
        examEndDate: null,
        examCenter: [],
        examCourse: [],
      };
    }

    const { data: fetchedExamCenters } = await refetchExamCenters();
    const examCenterList = fetchedExamCenters.reportGetAllExamCenters.examCenters;
    const selectedExamCenter = examCenter.includes('all')
      ? [{ label: `Select All (${examCenterList.length})`, value: 'all' }]
      : examCenterList
          .filter((ec) => examCenter.includes(ec.code))
          .map((ec) => ({ label: ec.code, value: ec.code }));

    const examCoursesList = examCourseData?.reportGetAllExamCourses?.examCourses ?? [];
    const selectedExamCourse = examCourse.includes('all')
      ? [{ label: `Select All (${examCoursesList.length})`, value: 'all' }]
      : examCoursesList
          .filter((c) => examCourse.includes(c.code))
          .map((c) => ({ label: c.code, value: c.code }));

    return {
      examStartDate,
      examEndDate,
      examCenter: selectedExamCenter,
      examCourse: selectedExamCourse,
    };
  }, [
    examStartDate,
    examEndDate,
    examCenter,
    examCourse,
    refetchExamCenters,
    examCourseData?.reportGetAllExamCourses?.examCourses,
  ]);

  useEffect(() => {
    const setFormValues = async () => {
      const formValues = await getFormValuesFromSearchParams();
      formContext.reset(formValues);
    };
    setFormValues();
  }, [formContext, getFormValuesFromSearchParams]);

  const onSubmit = (data: QuestionnaireReportFilterFormOutput) => {
    changeFilters({
      examStartDate: data.examStartDate,
      examEndDate: data.examEndDate,
      examCenter: data.examCenter.map((item) => item.value),
      examCourse: data.examCourse.map((item) => item.value),
    });
  };

  const handleReset = () => {
    formContext.reset({
      examStartDate: null,
      examEndDate: null,
      examCenter: [],
      examCourse: [],
    });
    resetAllParams();
  };

  const currentDate = new Date();

  return (
    <FormProvider {...formContext}>
      <Form
        sx={(theme) => ({
          p: theme.spacing(4),
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(4),
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: theme.shape.rounded(2),
        })}
        onSubmit={formContext.handleSubmit(onSubmit)}
      >
        <Grid sx={{ gridTemplateColumns: '160px 1fr' }}>
          <Text variant='body1Semibold' sx={{ flexBasis: '160px' }}>
            Date Filter:
          </Text>
          <Flex sx={(theme) => ({ gap: theme.spacing(4), alignItems: 'flex-start', flex: 1 })}>
            <Box sx={{ flexBasis: '256px' }}>
              <FormDateInput
                name='examStartDate'
                label='Start Date'
                placeholder='DD-MM-YYYY'
                inputFormat='DD MMM BBBB'
                labelFormat='MMMM BBBB'
                yearLabelFormat='BBBB'
                maxDate={currentDate}
                fullWidth
              />
            </Box>
            <Text sx={(theme) => ({ whiteSpace: 'nowrap', pt: theme.spacing(8) })}>to</Text>
            <Box sx={{ flexBasis: '256px' }}>
              <FormDateInput
                name='examEndDate'
                label='End Date'
                placeholder='DD-MM-YYYY'
                inputFormat='DD MMM BBBB'
                labelFormat='MMMM BBBB'
                yearLabelFormat='BBBB'
                maxDate={currentDate}
                fullWidth
              />
            </Box>
          </Flex>
        </Grid>

        <Grid sx={{ gridTemplateColumns: '160px 1fr' }}>
          <Text variant='body1Semibold' sx={{ flexBasis: '160px' }}>
            Other Filters:
          </Text>
          <Flex sx={(theme) => ({ gap: theme.spacing(4), flex: 1, alignItems: 'flex-start' })}>
            <Box sx={{ flexBasis: '256px' }}>
              <AllExamCenterSearchSelection
                name='examCenter'
                placeholder={
                  (formContext.watch('examCenter') ?? []).length === 0 ? 'เลือก Exam Center' : ''
                }
              />
            </Box>
            <Box sx={{ flexBasis: '256px' }}>
              <FormSearchMultipleSelectionWithSelectAllOption
                name='examCourse'
                label='Exam Course'
                loading={isLoadingExamCourses}
                slotProps={{
                  selectAllOption: {
                    label: `Select All (${examCourseOptions.length})`,
                  },
                }}
                placeholder={
                  (formContext.watch('examCourse') ?? []).length === 0 ? 'เลือก Exam Course' : ''
                }
                options={examCourseOptions}
              />
            </Box>
          </Flex>
        </Grid>

        <Divider />
        <Flex sx={(theme) => ({ gap: theme.spacing(2), justifyContent: 'flex-end' })}>
          <Button variant='outlined' color='secondary' onClick={handleReset}>
            Reset
          </Button>
          <Button type='submit'>Apply</Button>
        </Flex>
      </Form>
    </FormProvider>
  );
}
